// =============================================================================
// WIERSZ „OBIEG DOKUMENTÓW” NA KARCIE AGENTA RECEPTORA (UD-11a, plan 53 §13.6.4 p. 9)
//
// PIĘĆ WARIANTÓW, każdy NAZWANY i żaden nie udaje innego (logika wyboru:
// `wariantObiegu` w `obiegDokumentow.ts`, tu tylko rysowanie i akcje):
//
//   1. brak prawa podglądu  — rola spoza owner/admin/manager/super-admin. Baza
//      oddaje takiej osobie PUSTĄ listę kursorów, więc „nieaktywny” byłby
//      kłamstwem; panel w ogóle o kursor nie pyta.
//   2. nieaktywny           — brak kursora organizacji (albo kursor bez żywego
//      prowadzącego). **Włącz obieg dokumentów** dla owner, admin, super-admin.
//   3. aktywny              — kursor prowadzi TEN agent, stan `aktywny`.
//   4. zatrzymany           — kursor prowadzi TEN agent, stan inny niż `aktywny`.
//   5. inny agent           — kursor organizacji prowadzi INNY, żywy agent.
//
// Poza pięcioma są dwa stany ŁADOWANIA, bez których ekran skłamałby: „jeszcze
// nie wiadomo” (pierwszy odczyt w toku) i „nie udało się wczytać” (błąd odczytu
// to NIE jest „obieg nieaktywny”).
//
// CZEGO TEN WIERSZ NIGDY NIE ROBI: nie zleca zadań dokumentowych. Odczyt i
// potwierdzenia zleca wyłącznie harmonogram usługi (`zPanelu = false`).
// Przyciski to decyzje człowieka o obiegu — włącz, wstrzymaj, wznów, przepnij —
// i każda idzie przez trasę usługi, która sprawdza rolę drugi raz.
//
// ZAKŁAD NIEAKTYWNY (P2, pozycja 13.1 MOST_ZAKLADOW.md): okno włączenia widzi
// wyłącznie AKTYWNE zakłady (`listaZakladow`), a nazwę i status zakładu kursora
// bierze z osobnego odczytu (`zakladyKursorow`). Kursor na zakładzie usuniętym na
// ekranie Lokalizacje działa dalej — dochodzi tylko ostrzeżenie, a przyciski
// Wstrzymaj, Wznów i Przepnij zostają, bo nie wiążą nowego zakładu.
//
// Same dokumenty pokazuje lista „Organizacja › Obieg dokumentów” (UD-12a) —
// w wariancie aktywnym i zatrzymanym wiersz ma do niej odnośnik, bo tylko wtedy
// jest co oglądać. Słowa liczników i zdanie o porażkach są wspólne z listą
// (`obiegDokumentow.ts`), więc oba ekrany mówią o kursorze to samo.
// =============================================================================

import { Alert, Button, Input, Modal, ModalFooter, Select, Spinner } from '@insolitum/ui';
import { Pause, Play, Power, Shuffle } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { formatujCzasWzgledny } from '../printing/status';

import {
  ADRES_LISTY_DOKUMENTOW,
  NASTAWY_DOMYSLNE_OBIEGU,
  OKNO_OD_H,
  ZDANIA_STANOW,
  doPolaDatyLokalnej,
  etykietaZakladuPrzejecia,
  nazwaZakladuKursora,
  opisLicznikow,
  opisPorazekOdczytu,
  sposobWyjscia,
  wariantObiegu,
  walidujNastawy,
  walidujOd,
  walidujPowod,
  zdanieStanu,
  zdanieZaleglosciKontroli,
  zdaniePowoduNiezlecania,
  stanZakladuKursora,
  type KursorObiegu,
  type NazwyZakladow,
  type PrawaObiegu,
  type ZakladObiegu,
} from './obiegDokumentow';
import {
  KOMUNIKAT_BRAK_RECEPTOR_CORE,
  ReceptorCoreError,
  przepnijObieg,
  wlaczObieg,
  wstrzymajObieg,
  wznowObieg,
  type AgentReceptora,
} from './receptorCoreApi';

const dataGodzina = (iso: string): string => new Date(iso).toLocaleString('pl-PL');

export interface WierszObieguProps {
  organizationId: string;
  agent: AgentReceptora;
  /** Wszyscy agenci organizacji (z odwołanymi) — do rozstrzygnięcia, czy prowadzący kursor żyje. */
  agenci: readonly AgentReceptora[];
  prawa: PrawaObiegu;
  /** `null` = jeszcze nie wczytano, `'blad'` = odczyt się nie udał. */
  kursory: KursorObiegu[] | 'blad' | null;
  /** Zakłady do NOWEGO włączenia — wyłącznie aktywne (`listaZakladow`). */
  zaklady: ZakladObiegu[] | 'blad' | null;
  /** Nazwy i status zakładów KURSORÓW, z kluczem zbioru id (`zakladyOrganizacji`). */
  zakladyKursorow: NazwyZakladow;
  /** Ponowny odczyt zakładów — okno włączenia woła go, gdy lista się nie wczytała albo zakład okazał się nieaktywny. */
  ponowZaklady: () => void;
  /** Liczba dokumentów sprawdzonych dawniej niż 12 h; `'blad'` = nie udało się policzyć. */
  zalegle: number | 'blad' | null;
  teraz: number;
  zapisMozliwy: boolean;
  /** Po udanej akcji — ekran odświeża kursor. */
  onZmiana: () => void;
}

type Okno = 'wlacz' | 'wstrzymaj' | 'wznow' | 'przepnij' | null;

export function WierszObiegu({
  organizationId,
  agent,
  agenci,
  prawa,
  kursory,
  zaklady,
  zakladyKursorow,
  ponowZaklady,
  zalegle,
  teraz,
  zapisMozliwy,
  onZmiana,
}: WierszObieguProps) {
  const [okno, setOkno] = useState<Okno>(null);

  const ramka = (wariant: string, dzieci: ReactNode, stan?: string) => (
    <div
      className="mt-1 space-y-0.5 text-xs break-words"
      data-testid={`obieg-dokumentow-${agent.id}`}
      data-wariant={wariant}
      data-stan={stan}
    >
      {dzieci}
    </div>
  );

  // 1. Brak prawa podglądu — PRZED jakimkolwiek odczytem (kursory są wtedy
  // `null`, bo ekran w ogóle o nie nie pytał).
  const brakPrawa = () =>
    ramka(
      'brak_prawa',
      <p className="text-aurora-muted">
        Obieg dokumentów: stan obiegu widzi właściciel, administrator albo kierownik organizacji.
      </p>
    );
  if (!prawa.podglad) return brakPrawa();
  if (kursory === null) {
    return ramka('ladowanie', <p className="text-aurora-muted">Obieg dokumentów: wczytywanie…</p>);
  }
  if (kursory === 'blad') {
    return ramka(
      'blad_odczytu',
      <p className="text-aurora-warning">
        Obieg dokumentów: nie udało się wczytać stanu — odśwież listę.
      </p>
    );
  }

  const w = wariantObiegu({ prawa, kursory, agentId: agent.id, agenci });
  if (w.wariant === 'brak_prawa') return brakPrawa();
  const wylaczone = !zapisMozliwy;
  const podpowiedz = zapisMozliwy ? undefined : KOMUNIKAT_BRAK_RECEPTOR_CORE;

  const zamknij = (zapisano: boolean) => {
    setOkno(null);
    if (zapisano) onZmiana();
  };

  /**
   * Wiersz zakładu: nazwa · od · ostatni odczyt — wspólny dla „aktywny” i „zatrzymany”.
   * Zakład nieaktywny dostaje drugi akapit (T-a): kto decyduje — jak zakład przywrócić,
   * kto tylko ogląda — kogo o to poprosić. Nazwa to nazwa LOKALIZACJI, bo pod nią
   * most rozpozna zakład dodany ponownie (reguła 1, 791).
   */
  const liniaKursora = (k: KursorObiegu) => {
    const nazwa = nazwaZakladuKursora(zakladyKursorow, zaklady, k.zaklad_id);
    return (
      <>
        <p className="text-aurora-subtle">
          Zakład: {nazwa} · od {dataGodzina(k.od)} · ostatni odczyt:{' '}
          {k.ostatni_odczyt_at
            ? formatujCzasWzgledny(k.ostatni_odczyt_at, teraz)
            : 'jeszcze nie było'}
        </p>
        {stanZakladuKursora(zakladyKursorow, k.zaklad_id) === 'nieaktywny' && (
          <p className="text-aurora-warning" data-testid={`obieg-zaklad-nieaktywny-${agent.id}`}>
            Zakład „{nazwa}” jest nieaktywny — zwykle oznacza to, że usunięto go na ekranie{' '}
            <strong>Lokalizacje</strong>. Ten obieg zostaje przy nim, ale nowego obiegu w
            nieaktywnym zakładzie nie włączysz. Jeśli zakład usunięto na ekranie{' '}
            <strong>Lokalizacje</strong>,{' '}
            {prawa.decyduje
              ? `wróci, gdy dodasz go tam ponownie pod nazwą „${nazwa}”.`
              : `poproś właściciela albo administratora organizacji, żeby dodał go tam ponownie pod nazwą „${nazwa}”.`}
          </p>
        )}
      </>
    );
  };

  /** Odnośnik do listy dokumentów — wariant aktywny i zatrzymany (jest co oglądać). */
  const odnosnikListy = (
    <p>
      <Link
        to={ADRES_LISTY_DOKUMENTOW}
        className="text-aurora-brand underline"
        data-testid={`obieg-lista-${agent.id}`}
      >
        Dokumenty z Receptora
      </Link>
    </p>
  );

  const tylkoPodglad = !prawa.decyduje && (
    <p className="text-aurora-muted" data-testid={`obieg-tylko-podglad-${agent.id}`}>
      Ustawienia obiegu zmienia właściciel albo administrator organizacji.
    </p>
  );

  // 2. Nieaktywny (albo kursor bez żywego prowadzącego).
  if (w.wariant === 'nieaktywny') {
    const przejecie = w.przejecie;
    return ramka(
      'nieaktywny',
      <>
        <p className="text-aurora-muted">
          {przejecie
            ? 'Obieg dokumentów: włączony w tej organizacji, ale agent, który go prowadził, został odwołany albo usunięty. Włączenie przy tym agencie przeniesie obieg tutaj i zachowa pozycję odczytu.'
            : 'Obieg dokumentów: nieaktywny — dokumenty z Receptora nie są jeszcze odczytywane.'}
        </p>
        {prawa.decyduje &&
          (agent.status === 'active' ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setOkno('wlacz')}
              disabled={wylaczone}
              title={podpowiedz}
            >
              <Power className="w-4 h-4" />
              Włącz obieg dokumentów
            </Button>
          ) : (
            <p className="text-aurora-muted">Obieg można włączyć po sparowaniu agenta.</p>
          ))}
        {tylkoPodglad}
        {okno === 'wlacz' && (
          <OknoWlaczenia
            organizationId={organizationId}
            agent={agent}
            zaklady={zaklady}
            zakladyKursorow={zakladyKursorow}
            ponowZaklady={ponowZaklady}
            przejecie={przejecie}
            teraz={teraz}
            onZamknij={zamknij}
          />
        )}
      </>
    );
  }

  // 5. Kursor organizacji prowadzi inny agent.
  if (w.wariant === 'inny_agent') {
    const prowadzacy = agenci.find((a) => a.id === w.kursor.agent_id);
    return ramka(
      'inny_agent',
      <p className="text-aurora-muted">
        Obieg dokumentów: Ten serwer nie jest w obiegu — obieg dokumentów obsługuje jeden serwer na
        organizację{prowadzacy ? ` (teraz: ${prowadzacy.name})` : ''}.
      </p>
    );
  }

  const k = w.kursor;

  // 3. Aktywny.
  if (w.wariant === 'aktywny') {
    const liczniki = opisLicznikow(k.liczniki);
    const powod = zdaniePowoduNiezlecania(k.harmonogram_powod);
    const zaleglosc = typeof zalegle === 'number' ? zdanieZaleglosciKontroli(zalegle) : null;
    return ramka(
      'aktywny',
      <>
        <p className="text-aurora-success">Obieg dokumentów: {ZDANIA_STANOW.aktywny}</p>
        {liniaKursora(k)}
        {liczniki && <p className="text-aurora-subtle">Dokumenty: {liczniki}</p>}
        {powod && (
          <p className="text-aurora-warning" data-testid={`obieg-powod-${agent.id}`}>
            Odczyt nie jest teraz zlecany: {powod}
          </p>
        )}
        <p className={k.porazki_z_rzedu > 0 ? 'text-aurora-warning' : 'text-aurora-subtle'}>
          {opisPorazekOdczytu(k, teraz)}
        </p>
        {zaleglosc && (
          <p className="text-aurora-warning" data-testid={`obieg-zaleglosc-${agent.id}`}>
            {zaleglosc}
          </p>
        )}
        {zalegle === 'blad' && (
          <p className="text-aurora-muted">Zaległość kontroli: nie udało się sprawdzić.</p>
        )}
        {odnosnikListy}
        {prawa.decyduje && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setOkno('wstrzymaj')}
            disabled={wylaczone}
            title={podpowiedz}
          >
            <Pause className="w-4 h-4" />
            Wstrzymaj
          </Button>
        )}
        {tylkoPodglad}
        {okno === 'wstrzymaj' && (
          <OknoStanu
            tryb="wstrzymaj"
            organizationId={organizationId}
            agent={agent}
            kursor={k}
            onZamknij={zamknij}
          />
        )}
      </>,
      k.stan
    );
  }

  // 4. Zatrzymany.
  const wyjscie = sposobWyjscia(k.stan);
  return ramka(
    'zatrzymany',
    <>
      <p className="text-aurora-warning" data-testid={`obieg-stan-${agent.id}`}>
        Obieg dokumentów: {zdanieStanu(k.stan)}
      </p>
      {k.stan_powod && (
        <p className="text-aurora-subtle">
          Powód zatrzymania: {k.stan_powod}
          {k.stan_at ? ` (${dataGodzina(k.stan_at)})` : ''}
        </p>
      )}
      {liniaKursora(k)}
      {odnosnikListy}
      {wyjscie === 'wznow' && prawa.decyduje && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setOkno('wznow')}
          disabled={wylaczone}
          title={podpowiedz}
        >
          <Play className="w-4 h-4" />
          Wznów
        </Button>
      )}
      {wyjscie === 'przepiecie' && prawa.przepina && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setOkno('przepnij')}
          disabled={wylaczone}
          title={podpowiedz}
        >
          <Shuffle className="w-4 h-4" />
          Przepnij
        </Button>
      )}
      {wyjscie === 'wznow' && tylkoPodglad}
      {okno === 'wznow' && (
        <OknoStanu
          tryb="wznow"
          organizationId={organizationId}
          agent={agent}
          kursor={k}
          onZamknij={zamknij}
        />
      )}
      {okno === 'przepnij' && (
        <OknoPrzepiecia
          organizationId={organizationId}
          agent={agent}
          kursor={k}
          teraz={teraz}
          onZamknij={zamknij}
        />
      )}
    </>,
    k.stan
  );
}

// ── Wspólne drobiazgi okien ─────────────────────────────────────────────────

const komunikatBledu = (err: unknown, domyslny: string): string =>
  err instanceof ReceptorCoreError ? err.message : domyslny;

/**
 * Pole `od`: data i godzina z przeglądarki, BEZ wartości domyślnej. `od` ustala
 * numery dokumentów, które Receptor nada raz i nieodwracalnie — „teraz” wstawione
 * z góry byłoby decyzją, której człowiek nie podjął.
 */
function PoleOd({
  wartosc,
  onZmiana,
  teraz,
}: {
  wartosc: string;
  onZmiana: (v: string) => void;
  teraz: number;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-aurora-muted mb-1">Początek obiegu (od)</span>
      <Input
        type="datetime-local"
        value={wartosc}
        min={doPolaDatyLokalnej(teraz - OKNO_OD_H * 3_600_000)}
        max={doPolaDatyLokalnej(teraz)}
        onChange={(e) => onZmiana(e.target.value)}
      />
      <span className="block mt-1 text-xs text-aurora-muted">
        Data i godzina według Twojej przeglądarki. Nie wcześniej niż {OKNO_OD_H} godzin temu i nie w
        przyszłości.
      </span>
    </label>
  );
}

function PolePowodu({ wartosc, onZmiana }: { wartosc: string; onZmiana: (v: string) => void }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-aurora-muted mb-1">Powód</span>
      <Input
        value={wartosc}
        maxLength={500}
        onChange={(e) => onZmiana(e.target.value)}
        placeholder="Zostaje w śladzie obiegu"
      />
    </label>
  );
}

// ── Okno włączenia ──────────────────────────────────────────────────────────

function OknoWlaczenia({
  organizationId,
  agent,
  zaklady,
  zakladyKursorow,
  ponowZaklady,
  przejecie,
  teraz,
  onZamknij,
}: {
  organizationId: string;
  agent: AgentReceptora;
  zaklady: ZakladObiegu[] | 'blad' | null;
  zakladyKursorow: NazwyZakladow;
  ponowZaklady: () => void;
  /** Kursor bez żywego prowadzącego: zakład i `od` zostają z poprzedniego włączenia. */
  przejecie: KursorObiegu | null;
  teraz: number;
  onZamknij: (zapisano: boolean) => void;
}) {
  const [zakladId, setZakladId] = useState(przejecie?.zaklad_id ?? '');
  const [od, setOd] = useState('');
  const [karencja, setKarencja] = useState(String(NASTAWY_DOMYSLNE_OBIEGU.karencja_min));
  const [limit, setLimit] = useState(String(NASTAWY_DOMYSLNE_OBIEGU.limit_strony));
  const [limitCzasu, setLimitCzasu] = useState(String(NASTAWY_DOMYSLNE_OBIEGU.limit_czasu_s));
  const [pracuje, setPracuje] = useState(false);
  const [blad, setBlad] = useState<string | null>(null);

  // Lista zakładów, która się nie wczytała, dostaje drugie podejście — okno
  // bez zakładów nie ma czego zapisać, a człowiek nie wie, co poszło nie tak.
  useEffect(() => {
    // Raz, przy otwarciu okna — dlatego puste zależności.
    if (zaklady === 'blad') ponowZaklady();
  }, []);

  // Jeden zakład ze strefą = podpowiedź (nadal widoczna na liście, można ją zmienić).
  useEffect(() => {
    if (zakladId !== '' || !Array.isArray(zaklady)) return;
    if (zaklady.length === 1 && zaklady[0].timezone) setZakladId(zaklady[0].id);
  }, [zaklady, zakladId]);

  const lista = Array.isArray(zaklady) ? zaklady : [];
  const opcje = lista.map((z) => ({
    value: z.id,
    label: z.timezone ? z.name : `${z.name} — brak strefy czasowej`,
    disabled: !z.timezone,
  }));
  // Przejęcie kursora, którego zakładu nie ma na liście AKTYWNYCH (P2): zakład jest
  // przesądzony, więc pole pokazuje go jako wybraną, wyłączoną pozycję — inaczej
  // stałoby na „Wybierz zakład”, a funkcja i tak przyjmie to samo wiązanie.
  if (przejecie && !lista.some((z) => z.id === przejecie.zaklad_id)) {
    opcje.push({
      value: przejecie.zaklad_id,
      label: etykietaZakladuPrzejecia(zakladyKursorow, przejecie.zaklad_id),
      disabled: true,
    });
  }
  const przejecieNieaktywne =
    przejecie !== null && stanZakladuKursora(zakladyKursorow, przejecie.zaklad_id) === 'nieaktywny';
  const bezStrefy = lista.filter((z) => !z.timezone);

  const gotowe = zakladId !== '' && (przejecie !== null || od !== '');

  const zapisz = async () => {
    setBlad(null);
    let odIso: string;
    if (przejecie) {
      // Przeniesienie kursora zachowuje `od` i `po_seq` — wartość jedzie tylko
      // dlatego, że trasa wymaga pola; funkcja bazy jej w tej gałęzi nie czyta.
      odIso = przejecie.od;
    } else {
      const wOd = walidujOd(od, Date.now());
      if (!wOd.ok) return setBlad(wOd.powod);
      odIso = wOd.wartosc;
    }
    const nastawy = walidujNastawy({
      karencja_min: karencja,
      limit_strony: limit,
      limit_czasu_s: limitCzasu,
    });
    if (!nastawy.ok) return setBlad(nastawy.powod);

    setPracuje(true);
    try {
      await wlaczObieg(organizationId, agent.id, {
        zaklad_id: zakladId,
        od: odIso,
        ...nastawy.wartosc,
      });
      onZamknij(true);
    } catch (err) {
      setBlad(komunikatBledu(err, 'Nie udało się włączyć obiegu dokumentów'));
      // Zakład usunięty w międzyczasie (np. w drugiej karcie) — lista ma przestać
      // go pokazywać. Rozpoznanie po KODZIE z usługi, nie po treści zdania.
      if (err instanceof ReceptorCoreError && err.kod === 'zaklad_nieaktywny') ponowZaklady();
      setPracuje(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => onZamknij(false)}
      title="Włącz obieg dokumentów"
      description={`Agent „${agent.name}” zacznie odbierać dokumenty magazynowe z Receptora i rejestrować je w Universe.`}
      footer={
        <ModalFooter>
          <Button variant="ghost" onClick={() => onZamknij(false)} disabled={pracuje}>
            Anuluj
          </Button>
          <Button onClick={() => void zapisz()} disabled={!gotowe || pracuje}>
            {pracuje ? <Spinner size="sm" /> : <Power className="w-4 h-4" />}
            Włącz
          </Button>
        </ModalFooter>
      }
    >
      <div className="space-y-4">
        {blad && <Alert variant="error">{blad}</Alert>}

        {przejecie && (
          <Alert variant="info">
            Obieg jest już włączony w tej organizacji — zakład i początek obiegu zostają z
            poprzedniego włączenia (od {dataGodzina(przejecie.od)}), a pozycja odczytu się nie cofa.
            Zmienić możesz tylko nastawy poniżej.
          </Alert>
        )}
        {przejecieNieaktywne && (
          <p className="text-xs text-aurora-warning" data-testid="obieg-przejecie-nieaktywny">
            Zakład tego obiegu jest nieaktywny. Obieg zostanie przy nim — przy przejęciu nie
            wybierasz nowego zakładu.
          </p>
        )}

        {zaklady === 'blad' && (
          <Alert variant="error">
            Nie udało się wczytać zakładów organizacji — spróbuj ponownie za chwilę.
          </Alert>
        )}
        {!przejecie && Array.isArray(zaklady) && zaklady.length === 0 && (
          <Alert variant="warning">
            Organizacja nie ma aktywnego zakładu. Dodaj go na ekranie <strong>Lokalizacje</strong>{' '}
            (menu administracji, przycisk <strong>Dodaj lokalizację</strong>). Po zapisaniu wróć
            tutaj i odśwież listę. Dokumenty są przypisywane do zakładu, więc bez niego nie włączysz
            obiegu.
            <div className="mt-2">
              <Button size="sm" variant="secondary" onClick={ponowZaklady}>
                Odśwież listę zakładów
              </Button>
            </div>
          </Alert>
        )}
        {bezStrefy.length > 0 && (
          <p className="text-xs text-aurora-muted">
            Zakład bez strefy czasowej nie może prowadzić obiegu — data operacji dokumentu liczy się
            w strefie zakładu. Strefy zakładu nie ustawisz dziś na żadnym ekranie — zgłoś to w
            sekcji Wsparcie, podając nazwę zakładu.
          </p>
        )}

        <Select
          label="Zakład"
          placeholder={zaklady === null ? 'Wczytywanie zakładów…' : 'Wybierz zakład'}
          options={opcje}
          value={zakladId}
          onChange={(e) => setZakladId(e.target.value)}
          disabled={przejecie !== null || zaklady === null}
        />

        {!przejecie && (
          <>
            <PoleOd wartosc={od} onZmiana={setOd} teraz={teraz} />
            <p className="text-xs text-aurora-warning">
              Numery dokumentów od tej chwili Receptor nada raz i nieodwracalnie — dlatego tej
              wartości nie podpowiadamy.
            </p>
          </>
        )}

        <div className="grid grid-cols-3 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-aurora-muted mb-1">Karencja (min)</span>
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              max={1440}
              value={karencja}
              onChange={(e) => setKarencja(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-aurora-muted mb-1">
              Limit strony (dokumentów)
            </span>
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              max={100}
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-aurora-muted mb-1">
              Limit czasu odczytu (s)
            </span>
            <Input
              type="number"
              inputMode="numeric"
              min={30}
              max={300}
              value={limitCzasu}
              onChange={(e) => setLimitCzasu(e.target.value)}
            />
          </label>
        </div>
        <p className="text-xs text-aurora-muted">
          Karencja to czas, przez który świeży dokument czeka w Receptorze, zanim Universe go
          odbierze. Przy 0 dokument jedzie od razu, a poprawka po przekazaniu idzie korektą.
          Wartości domyślne są dobre na początek.
        </p>
      </div>
    </Modal>
  );
}

// ── Okno wstrzymania i wznowienia ───────────────────────────────────────────

function OknoStanu({
  tryb,
  organizationId,
  agent,
  kursor,
  onZamknij,
}: {
  tryb: 'wstrzymaj' | 'wznow';
  organizationId: string;
  agent: AgentReceptora;
  kursor: KursorObiegu;
  onZamknij: (zapisano: boolean) => void;
}) {
  const [powod, setPowod] = useState('');
  const [pracuje, setPracuje] = useState(false);
  const [blad, setBlad] = useState<string | null>(null);
  const wstrzymanie = tryb === 'wstrzymaj';

  const zapisz = async () => {
    const p = walidujPowod(powod);
    if (!p.ok) return setBlad(p.powod);
    setPracuje(true);
    setBlad(null);
    try {
      const wejscie = { powod: p.wartosc, zaklad_id: kursor.zaklad_id };
      if (wstrzymanie) await wstrzymajObieg(organizationId, agent.id, wejscie);
      else await wznowObieg(organizationId, agent.id, wejscie);
      onZamknij(true);
    } catch (err) {
      setBlad(
        komunikatBledu(
          err,
          wstrzymanie ? 'Nie udało się wstrzymać obiegu' : 'Nie udało się wznowić obiegu'
        )
      );
      setPracuje(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => onZamknij(false)}
      title={wstrzymanie ? 'Wstrzymaj obieg dokumentów' : 'Wznów obieg dokumentów'}
      description={
        wstrzymanie
          ? 'Odczyt dokumentów zatrzyma się w najbliższym pulsie agenta (do minuty). Pozycja odczytu zostaje — po wznowieniu obieg czyta dalej od tego miejsca.'
          : 'Obieg wróci do odczytu od zapamiętanej pozycji. Wznów go dopiero po usunięciu przyczyny zatrzymania.'
      }
      footer={
        <ModalFooter>
          <Button variant="ghost" onClick={() => onZamknij(false)} disabled={pracuje}>
            Anuluj
          </Button>
          <Button onClick={() => void zapisz()} disabled={powod.trim() === '' || pracuje}>
            {pracuje ? (
              <Spinner size="sm" />
            ) : wstrzymanie ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            {wstrzymanie ? 'Wstrzymaj obieg' : 'Wznów obieg'}
          </Button>
        </ModalFooter>
      }
    >
      <div className="space-y-4">
        {blad && <Alert variant="error">{blad}</Alert>}
        <PolePowodu wartosc={powod} onZmiana={setPowod} />
      </div>
    </Modal>
  );
}

// ── Okno przepięcia (wyłącznie super-admin) ─────────────────────────────────

function OknoPrzepiecia({
  organizationId,
  agent,
  kursor,
  teraz,
  onZamknij,
}: {
  organizationId: string;
  agent: AgentReceptora;
  kursor: KursorObiegu;
  teraz: number;
  onZamknij: (zapisano: boolean) => void;
}) {
  const [od, setOd] = useState('');
  const [powod, setPowod] = useState('');
  const [pracuje, setPracuje] = useState(false);
  const [blad, setBlad] = useState<string | null>(null);

  const zapisz = async () => {
    const wOd = walidujOd(od, Date.now());
    if (!wOd.ok) return setBlad(wOd.powod);
    const p = walidujPowod(powod);
    if (!p.ok) return setBlad(p.powod);
    setPracuje(true);
    setBlad(null);
    try {
      await przepnijObieg(organizationId, agent.id, {
        od: wOd.wartosc,
        powod: p.wartosc,
        zaklad_id: kursor.zaklad_id,
      });
      onZamknij(true);
    } catch (err) {
      setBlad(komunikatBledu(err, 'Nie udało się przepiąć obiegu'));
      setPracuje(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => onZamknij(false)}
      title="Przepnij obieg dokumentów"
      description="Obieg zacznie czytać od zera z nową instalacją Receptora. Stary stan, instalacja i pozycja odczytu zostają w śladzie."
      footer={
        <ModalFooter>
          <Button variant="ghost" onClick={() => onZamknij(false)} disabled={pracuje}>
            Anuluj
          </Button>
          <Button
            onClick={() => void zapisz()}
            disabled={od === '' || powod.trim() === '' || pracuje}
          >
            {pracuje ? <Spinner size="sm" /> : <Shuffle className="w-4 h-4" />}
            Przepnij obieg
          </Button>
        </ModalFooter>
      }
    >
      <div className="space-y-4">
        {blad && <Alert variant="error">{blad}</Alert>}
        <PoleOd wartosc={od} onZmiana={setOd} teraz={teraz} />
        <PolePowodu wartosc={powod} onZmiana={setPowod} />
      </div>
    </Modal>
  );
}
